import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import portalTranslations from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  Compass, GraduationCap, Search, Cog, FlaskConical,
  TrendingUp, Brain, Heart, Radar as RadarIcon, Network, ShieldAlert,
  BadgeDollarSign, ClipboardList, Target, Bot, Zap, PiggyBank, UserCheck,
  MessageSquare, Send, Loader, Activity, Bell, BarChart3,
  Mic, MicOff, Volume2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SpeechRecognition = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

const TOOL_ICONS = {
  coach: GraduationCap, explorer: Search, automation: Cog, simulator: FlaskConical,
  cashflow: PiggyBank, hiring: UserCheck,
  sales: TrendingUp, knowledge: Brain, pulse: Heart, radar: RadarIcon,
  mapper: Network, risk: ShieldAlert, pricing: BadgeDollarSign, meeting: ClipboardList,
  roadmap: Target, agent: Bot, process: Zap, training: GraduationCap,
  readiness: BarChart3, health: Activity, notifications: Bell,
};

const TOOL_COLORS = {
  coach: 'purple', explorer: 'blue', automation: 'orange', simulator: 'green',
  cashflow: 'green', hiring: 'blue',
  sales: 'blue', knowledge: 'purple', pulse: 'pink', radar: 'green',
  mapper: 'blue', risk: 'orange', pricing: 'green', meeting: 'purple',
  roadmap: 'purple', agent: 'blue', process: 'orange', training: 'green',
  readiness: 'blue', health: 'green', notifications: 'orange',
};

const TOOL_PATHS = {
  coach: '/portal/ai-tools/coach', explorer: '/portal/ai-tools/explorer',
  automation: '/portal/ai-tools/automation', simulator: '/portal/ai-tools/simulator',
  cashflow: '/portal/ai-tools/cashflow', hiring: '/portal/ai-tools/hiring',
  sales: '/portal/ai-tools/sales', knowledge: '/portal/ai-tools/knowledge',
  pulse: '/portal/ai-tools/pulse', radar: '/portal/ai-tools/radar',
  mapper: '/portal/ai-tools/mapper', risk: '/portal/ai-tools/risk',
  pricing: '/portal/ai-tools/pricing', meeting: '/portal/ai-tools/meeting',
  roadmap: '/portal/roadmap-creator', agent: '/portal/agent-builder',
  process: '/portal/process-optimizer', training: '/portal/virtual-training',
  readiness: '/portal/ai-tools/health-check', health: '/portal/ai-tools/financial-dashboard',
  notifications: '/portal/ai-tools/smart-alerts',
};

// New category structure as requested by user
const CATEGORIES = {
  nl: {
    company_health: { label: 'BEDRIJFSGEZONDHEID', icon: Activity },
    growth: { label: 'GROEI', icon: TrendingUp },
    strategy: { label: 'STRATEGIE', icon: Target },
    operations: { label: 'OPERATIES', icon: Cog },
    people: { label: 'MENSEN', icon: UserCheck },
    knowledge_cat: { label: 'KENNIS', icon: Brain },
  },
  en: {
    company_health: { label: 'COMPANY HEALTH', icon: Activity },
    growth: { label: 'GROWTH', icon: TrendingUp },
    strategy: { label: 'STRATEGY', icon: Target },
    operations: { label: 'OPERATIONS', icon: Cog },
    people: { label: 'PEOPLE', icon: UserCheck },
    knowledge_cat: { label: 'KNOWLEDGE', icon: Brain },
  }
};

const CATEGORY_TOOLS = {
  company_health: ['readiness', 'health', 'notifications'],
  growth: ['sales', 'radar', 'pricing'],
  strategy: ['explorer', 'simulator', 'risk', 'cashflow'],
  operations: ['automation', 'mapper', 'meeting'],
  people: ['coach', 'hiring'],
  knowledge_cat: ['knowledge', 'pulse'],
};

// Extra tool definitions not in portal.js yet
const EXTRA_TOOL_LABELS = {
  nl: {
    readiness: { title: 'AI Business Health Check', desc: 'Laat AI je bedrijf doorlichten met scores en verkeerslichten.' },
    health: { title: 'AI Financial Dashboard', desc: 'AI-analyse van je financiele KPIs met benchmarks en optimalisatie.' },
    notifications: { title: 'AI Smart Alerts', desc: 'Proactieve waarschuwingen en actiepunten op basis van je data.' },
  },
  en: {
    readiness: { title: 'AI Business Health Check', desc: 'Let AI scan your business with scores and traffic lights.' },
    health: { title: 'AI Financial Dashboard', desc: 'AI analysis of your financial KPIs with benchmarks and optimization.' },
    notifications: { title: 'AI Smart Alerts', desc: 'Proactive alerts and action items based on your data.' },
  }
};

// AI Tool Advisor - maps questions to tools
const TOOL_ADVISOR_MAP = {
  cashflow: ['cashflow', 'geld', 'liquiditeit', 'betalen', 'factuur', 'kas', 'cash', 'money', 'payment'],
  simulator: ['beslissing', 'wat als', 'simuleer', 'scenario', 'decision', 'what if', 'simulate'],
  hiring: ['aannemen', 'cv', 'sollicitant', 'kandidaat', 'hire', 'recruit', 'vacature', 'personeel'],
  sales: ['verkoop', 'leads', 'klanten', 'omzet', 'sales', 'revenue', 'deal'],
  explorer: ['data', 'analyse', 'inzicht', 'patronen', 'insight', 'analyze', 'csv'],
  automation: ['automatiseren', 'repetitief', 'workflow', 'efficienter', 'automate'],
  risk: ['risico', 'gevaar', 'bedreiging', 'risk', 'threat', 'compliance'],
  pricing: ['prijs', 'tarief', 'marge', 'price', 'margin', 'pricing'],
  knowledge: ['document', 'kennis', 'vraag', 'handleiding', 'knowledge', 'manual'],
  pulse: ['klant', 'feedback', 'tevredenheid', 'review', 'customer', 'satisfaction'],
  coach: ['leren', 'training', 'coach', 'advies', 'learn', 'mentor'],
  mapper: ['proces', 'workflow', 'stappen', 'bottleneck', 'process', 'mapping'],
  meeting: ['vergadering', 'meeting', 'notulen', 'actiepunten', 'notes'],
  radar: ['kans', 'groei', 'markt', 'opportunity', 'growth', 'market'],
};

const AIToolsDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { lang } = useLanguage();
  const t = portalTranslations[lang];
  const [advisorInput, setAdvisorInput] = useState('');
  const [advisorResult, setAdvisorResult] = useState(null);
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const recognitionRef = useRef(null);

  const speakText = async (text) => {
    const clean = ((text) == null ? '' : String(text)).replace(/\*\*|__|[*_`#\[\]()]/g, '').replace(/\n+/g, '. ').trim();
    if (!clean) return;
    setIsSpeaking(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/tts/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: clean, lang }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { setIsSpeaking(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setIsSpeaking(false); URL.revokeObjectURL(url); };
      audio.play();
      window._hyperAdvisorAudio = audio;
    } catch {
      setIsSpeaking(false);
    }
  };

  const stopSpeaking = () => {
    if (window._hyperAdvisorAudio) {
      window._hyperAdvisorAudio.pause();
      window._hyperAdvisorAudio = null;
    }
    setIsSpeaking(false);
  };

  const startListening = () => {
    if (!SpeechRecognition) return;
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = lang === 'nl' ? 'nl-NL' : 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results).map(r => r[0].transcript).join('');
      setAdvisorInput(transcript);
      if (Array.from(event.results).some(r => r.isFinal) && ((transcript) == null ? '' : String(transcript)).trim()) {
        setIsListening(false);
        setTimeout(() => askAdvisorDirect(((transcript) == null ? '' : String(transcript)).trim()), 200);
      }
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
  };

  const getToolInfo = (id) => {
    const extra = EXTRA_TOOL_LABELS[lang]?.[id];
    if (extra) return extra;
    return t.tools[id] || { title: id, desc: '' };
  };

  const askAdvisorDirect = async (text) => {
    setAdvisorInput(text);
    const q = ((text) == null ? '' : String(text)).toLowerCase();

    // Local keyword matching first
    let bestMatch = null;
    let bestScore = 0;
    for (const [toolId, keywords] of Object.entries(TOOL_ADVISOR_MAP)) {
      const score = (Array.isArray(keywords) ? keywords : []).filter(kw => (Array.isArray(q) ? q : []).includes(kw)).length;
      if (score > bestScore) {
        bestScore = score;
        bestMatch = toolId;
      }
    }

    if (bestMatch && bestScore > 0) {
      const info = getToolInfo(bestMatch);
      const path = TOOL_PATHS[bestMatch];
      const msg = lang === 'nl'
        ? `Op basis van je vraag raad ik **${info.title}** aan. ${info.desc}`
        : `Based on your question, I recommend **${info.title}**. ${info.desc}`;
      setAdvisorResult({
        toolId: bestMatch, toolTitle: info.title, toolDesc: info.desc, path, message: msg,
      });
      speakText(msg);
      return;
    }

    // Fallback: ask AI
    setAdvisorLoading(true);
    try {
      const toolList = Object.entries(TOOL_ADVISOR_MAP).map(([id]) => {
        const info = getToolInfo(id);
        return `- ${id}: ${info.title} - ${info.desc}`;
      }).join('\n');

      const res = await axios.post(`${BACKEND_URL}/api/compass/simulator/quick-decision`, {
        question: `Een gebruiker vraagt: "${text}"\n\nWelke tool uit deze lijst past het beste?\n${toolList}\n\nAntwoord met ALLEEN de tool ID (bijv: cashflow, simulator, hiring) gevolgd door een korte uitleg waarom.`,
        language: lang,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const analysis = res.data.analysis || '';
      let matchedTool = null;
      for (const toolId of Object.keys(TOOL_ADVISOR_MAP)) {
        if (((analysis) == null ? '' : String(analysis)).toLowerCase().includes(toolId)) {
          matchedTool = toolId;
          break;
        }
      }

      if (matchedTool) {
        const info = getToolInfo(matchedTool);
        setAdvisorResult({
          toolId: matchedTool, toolTitle: info.title, toolDesc: info.desc,
          path: TOOL_PATHS[matchedTool], message: analysis,
        });
        speakText(analysis);
      } else {
        setAdvisorResult({ toolId: null, message: analysis });
        speakText(analysis);
      }
    } catch {
      const errMsg = lang === 'nl' ? 'Kon geen aanbeveling genereren. Kies een tool uit het overzicht hieronder.' : 'Could not generate recommendation. Choose a tool from the overview below.';
      setAdvisorResult({ toolId: null, message: errMsg });
    } finally {
      setAdvisorLoading(false);
    }
  };

  const askAdvisor = () => askAdvisorDirect(advisorInput);

  const categories = CATEGORIES[lang] || CATEGORIES.en;

  return (
    <div className="tool-page">
      <div className="tool-header">
        <h1 className="heading-1" data-testid="ai-tools-title">
          <Compass size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} />
          {t.aiToolsTitle}
        </h1>
      </div>

      <div className="tool-content">
        {/* AI Tool Advisor */}
        <div className="advisor-bar" data-testid="ai-tool-advisor">
          <div className="advisor-icon">
            <MessageSquare size={20} />
          </div>
          <div className="advisor-content">
            <h3 className="body-small" style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
              {lang === 'nl' ? 'Welke tool heb ik nodig?' : 'Which tool do I need?'}
            </h3>
            <form className="advisor-form" onSubmit={(e) => { e.preventDefault(); askAdvisor(); }}>
              <button
                type="button"
                onClick={startListening}
                className={`voice-mic-btn ${isListening ? 'listening' : ''}`}
                data-testid="advisor-mic-btn"
                title={isListening ? (lang === 'nl' ? 'Stop luisteren' : 'Stop listening') : (lang === 'nl' ? 'Spreek je vraag in' : 'Speak your question')}
                style={{ width: '2.75rem', height: '2.75rem', flexShrink: 0 }}
              >
                {isListening ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
              <input
                type="text"
                className="form-input"
                placeholder={lang === 'nl' ? 'Bijv: "Ik wil mijn cashflow voorspellen" of klik op de microfoon' : 'E.g.: "I want to predict my cashflow" or click the microphone'}
                value={advisorInput}
                onChange={(e) => setAdvisorInput(e.target.value)}
                data-testid="advisor-input"
              />
              <button type="submit" className="btn-primary" disabled={advisorLoading || !((advisorInput) == null ? '' : String(advisorInput)).trim()} data-testid="advisor-submit">
                {advisorLoading ? <Loader className="spin" size={16} /> : <Send size={16} />}
              </button>
            </form>
            {isListening && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                color: '#ef4444', fontSize: '0.8rem', fontWeight: 600, marginTop: 6,
                animation: 'pulse 1.5s infinite',
              }} data-testid="advisor-listening">
                <Mic size={14} /> {lang === 'nl' ? 'Luisteren...' : 'Listening...'}
              </div>
            )}
            {advisorResult && (
              <div className="advisor-result" data-testid="advisor-result">
                <div className="body-small" style={{ marginBottom: advisorResult.path ? '0.5rem' : 0 }}>
                  <ReactMarkdown>{advisorResult.message}</ReactMarkdown>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {advisorResult.path && (
                    <button className="btn-primary" style={{ fontSize: '0.8rem', padding: '0.4rem 1rem' }}
                      onClick={() => navigate(advisorResult.path)} data-testid="advisor-open-tool">
                      {lang === 'nl' ? 'Open' : 'Open'} {advisorResult.toolTitle}
                    </button>
                  )}
                  {isSpeaking ? (
                    <button onClick={stopSpeaking} className="btn-secondary"
                      style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}
                      data-testid="advisor-stop-speaking">
                      <Volume2 size={14} /> {lang === 'nl' ? 'Stop' : 'Stop'}
                    </button>
                  ) : (
                    <button onClick={() => speakText(advisorResult.message)} className="btn-secondary"
                      style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem', display: 'flex', alignItems: 'center', gap: 4, opacity: 0.7 }}
                      data-testid="advisor-replay">
                      <Volume2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tool Categories */}
        <div className="tools-categories" data-testid="tools-categories">
          {Object.entries(CATEGORY_TOOLS).map(([catKey, toolIds]) => {
            const cat = categories[catKey];
            if (!cat) return null;
            const CatIcon = cat.icon;
            return (
              <div key={catKey} className="tool-category-section" data-testid={`category-${catKey}`}>
                <div className="category-header">
                  <CatIcon size={16} />
                  <span>{cat.label}</span>
                </div>
                <div className="category-tools-grid">
                  {(Array.isArray(toolIds) ? toolIds : []).map(id => {
                    const info = getToolInfo(id);
                    const Icon = TOOL_ICONS[id] || Compass;
                    const color = TOOL_COLORS[id] || 'blue';
                    const path = TOOL_PATHS[id];
                    return (
                      <div key={id}
                        className={`portal-tool-card tool-${color}`}
                        onClick={() => navigate(path)}
                        data-testid={`compass-tool-${id}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="tool-icon"><Icon size={24} /></div>
                        <h3 className="heading-3">{info.title}</h3>
                        <p className="body-small text-secondary">{info.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AIToolsDashboard;
